import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Table, Text } from '@ui';
import { DispatchedAtCell, JoinCell, PlatformReceivedCell, PlatformStatusCell, PlatformTitleCell, QueuedAtCell, RailsDecisionCell, WhatChangedCell, WhenCell } from './PushHistoryCells';
import { PushHistoryDetail } from './PushHistoryDetail';
import { t } from './labels';
const PAGE_SIZE_OPTIONS = [25, 50, 100];
function ColumnLabel({ label, group }) {
    return (React.createElement("span", { className: "push-history__th" },
        React.createElement(Text, { variant: "xx-small", className: classNames('push-history__group', group && `push-history__group--${group}`) }, group ? t(`groups.${group}`) : ' '),
        label));
}
/** One row per push; Rails columns, the joining notification id, then the platform columns. */
export function PushHistoryTable({ rows, timeZone, platformUnavailable, tinted, expanded, onToggleRow }) {
    const columns = useMemo(() => [
        {
            dataKey: 'when',
            label: React.createElement(ColumnLabel, { label: t('columns.when') }),
            renderer: ({ item }) => React.createElement(WhenCell, { row: item, timeZone: timeZone, expanded: !!expanded[item.id] })
        },
        {
            dataKey: 'type',
            label: React.createElement(ColumnLabel, { label: t('columns.type') }),
            renderer: ({ item }) => React.createElement(Text, null, item.typeLabel)
        },
        {
            dataKey: 'decision',
            label: React.createElement(ColumnLabel, { label: t('columns.decision'), group: "rails" }),
            renderer: ({ item }) => React.createElement(RailsDecisionCell, { row: item })
        },
        {
            dataKey: 'what_changed',
            label: React.createElement(ColumnLabel, { label: t('columns.what_changed') }),
            maxWidth: '30rem',
            renderer: ({ item }) => React.createElement(WhatChangedCell, { row: item, timeZone: timeZone })
        },
        {
            dataKey: 'notification_id',
            label: React.createElement(ColumnLabel, { label: t('columns.notification_id'), group: "join" }),
            align: 'center',
            renderer: ({ item }) => React.createElement(JoinCell, { row: item })
        },
        {
            dataKey: 'status',
            label: React.createElement(ColumnLabel, { label: t('columns.status'), group: "platform" }),
            renderer: ({ item }) => React.createElement(PlatformStatusCell, { row: item, platformUnavailable: platformUnavailable })
        },
        {
            dataKey: 'title',
            label: React.createElement(ColumnLabel, { label: t('columns.title') }),
            maxWidth: '22rem',
            renderer: ({ item }) => React.createElement(PlatformTitleCell, { row: item })
        },
        {
            dataKey: 'created_at',
            label: React.createElement(ColumnLabel, { label: t('columns.created_at') }),
            renderer: ({ item }) => React.createElement(QueuedAtCell, { row: item, timeZone: timeZone })
        },
        {
            dataKey: 'dispatched_at',
            label: React.createElement(ColumnLabel, { label: t('columns.dispatched_at') }),
            renderer: ({ item }) => React.createElement(DispatchedAtCell, { row: item, timeZone: timeZone })
        },
        {
            dataKey: 'received',
            label: React.createElement(ColumnLabel, { label: t('columns.received') }),
            renderer: ({ item }) => React.createElement(PlatformReceivedCell, { row: item, timeZone: timeZone })
        }
    ], [timeZone, platformUnavailable, expanded]);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const paginationLabels = useMemo(() => ({
        page: t('pagination.page'),
        of: t('pagination.of'),
        itemsPerPage: t('pagination.items_per_page'),
        pageInputAriaLabel: t('pagination.page'),
        pageSizeAriaLabel: t('pagination.page_size'),
        firstPage: t('pagination.first_page'),
        previousPage: t('pagination.previous_page'),
        nextPage: t('pagination.next_page'),
        lastPage: t('pagination.last_page')
    }), []);
    const renderDetail = ({ item }) => (React.createElement(PushHistoryDetail, { row: item, timeZone: timeZone, platformUnavailable: platformUnavailable, columnCount: columns.length }));
    return (React.createElement("div", { className: "push-history__table-card" },
        React.createElement(Table, { className: classNames('push-history__table', tinted && 'push-history__table--tinted'), data: rows, columns: columns, getItemId: (row) => row.id, expandedRows: expanded, onRowClick: ({ item }) => onToggleRow(item.id), expandableRowRenderer: renderDetail, hover: true, paginated: true, pageSize: pageSize, onPageSizeChange: setPageSize, pageSizeOptions: PAGE_SIZE_OPTIONS, paginationLabels: paginationLabels })));
}
